import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useVoiceAssistant } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import {
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import log from '@/common/utils/logger';

import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamDriver as ExamDriver } from '@/common/api/models/oralExamDriver';
import { OralExamMediaSource } from '@/common/api/models/oralExamMediaSource';
import {
  clientRestorableOpenSources,
  isRecordingIntegrityOpen,
  isRecordingIntegrityOpenInRunState,
} from '@/common/oralExam/mediaIntegritySources';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { isSelfServiceRun, oralExamAskedQuestionCount, runRequiresEvaluation } from '@/common/utils/oralExam/runConduct';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import type { AIPresenceState } from '@/common/components/presence/types';
import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { SessionVoidTechnicalScreen } from '@/common/components/oralExam/SessionVoidTechnicalScreen';
import {
  resolveStudentCtaGate,
} from '@/common/hooks/oralExam/oralExamCtaGateMatrix';
import { useExaminerAgentPresent } from '@/common/hooks/oralExam/useExaminerAgentPresent';
import {
  useExaminerAgentWatchdog,
  shouldExaminerUnavailableHardStop,
  isExaminerColdStartConductPhase,
} from '@/common/hooks/oralExam/useExaminerAgentWatchdog';
import {
  AgentUnavailableTerminalScreen,
  shouldShowAgentUnavailableTerminal,
} from '@/common/components/oralExam/AgentUnavailableTerminalScreen';
import { isSupervisorEngagedLecturerPause } from '@/common/oralExam/supervisionPause';

import { integrityBudgetPauseRemainingSeconds } from '../utils/integrityBudgetPauseRemainingSeconds';
import {
  AiExaminerPresence,
  hasLiveAgentAudioTrack,
  resolveExaminerOrbState,
} from './AiExaminerPresence';
import {
  AuthoritativeCountdown,
  COMPACT_COUNTDOWN_RING_SIZE,
  COMPACT_COUNTDOWN_RING_SIZE_NARROW,
} from './AuthoritativeCountdown';
import { DoneAffordance, type OralExamDriver } from './DoneAffordance';
import { NeedHelpButton } from './NeedHelpButton';
import { PauseOverlay } from './PauseOverlay';
import { QuestionHero } from './QuestionHero';
import { QuestionProgressStrip } from './QuestionProgressStrip';
import {
  COHORT_QUESTION_COLUMN_PT,
  QUESTION_COLUMN_PR_PX,
  QUESTION_COLUMN_PT,
  resolveCohortQuestionColumnPrPx,
  SupervisorFrame,
  type SupervisorFaceDisplay,
} from './SupervisorFrame';
import { ReconnectOverlay, useTransportReconnectOverlayVisible } from './ReconnectOverlay';
import { SelfViewChip } from './SelfViewChip';
import { useStudentExamAbortConfirm } from '../utils/studentExamAbortConfirm';
import {
  BETWEEN_QUESTIONS_REVEAL_MS,
  deriveStudentExamShellPhase,
  studentExamShellPhaseTestId,
  useDisplayedExamShellPhase,
  type StudentExamShellPhase,
} from '../utils/deriveStudentRuntimeJourney';

const PRESENCE_RAIL_WIDTH = 220;
const PRESENCE_RAIL_WIDTH_NARROW = 152;
const QUESTION_ACTIVE_ORB_SIZE = 112;
const QUESTION_ACTIVE_ORB_SIZE_NARROW = 80;
const QUESTION_ACTIVE_ORB_SIZE_COMPACT = 60;
const BETWEEN_QUESTIONS_ORB_SIZE = 112;
const SHELL_NARROW_MAX_WIDTH = 1100;
const SHELL_NARROW_MAX_HEIGHT = 760;
const RAIL_DEFAULT_TOP_INSET_PX = 32;
/** Below this the supervisor frame shrinks to the self-view tile size. */
const SHELL_COMPACT_MAX_WIDTH = 960;
const SHELL_COMPACT_MAX_HEIGHT = 600;

export type StudentExamShellProps = {
  runState: OralExamRunStateResponse;
  studentFirstName: string;
  driver?: OralExamDriver;
  onStartExam?: () => void;
  isMarkingStudentReady?: boolean;
  startExamError?: string | null;
  studentDoneError?: string | null;
  onStudentDone?: () => void;
  onOpenAnswerWindow?: () => void | Promise<void>;
  onRequestHelp?: () => void;
  isSignalingDone?: boolean;
  isRequestingHelp?: boolean;
  isReconnecting?: boolean;
  /** Raw LiveKit connection state — drives integrity pause vs transport overlay (§3.6). */
  liveKitConnectionState?: ConnectionState;
  isScreenSharing?: boolean;
  bookingId?: string;
  supervisorName?: string;
  supervisorPhotoUrl?: string | number | { uri?: string } | null;
  supervisorFaces?: SupervisorFaceDisplay[];
  visitingLecturerId?: string | null;
  openMediaSources?: OralExamMediaSource[];
  permissionDeniedSource?: OralExamMediaSource | null;
  onReshareScreen?: () => void;
  onEnableCamera?: () => void;
  onUnmuteMic?: () => void;
  onAbort?: () => void | Promise<void>;
  isAborting?: boolean;
  /** Leave the session without completing the exam (rejoin path). */
  onLeave?: () => void;
  /** Self-service dry-run: ensure + navigate into a fresh lobby after agent_unavailable. */
  onStartPractice?: () => void | Promise<void>;
  isStartingPractice?: boolean;
  startPracticeError?: string | null;
};

function orbActivatesVoiceAssistant(phase: StudentExamShellPhase): boolean {
  // Keep the shared orb live for the whole in-room shell so examiner speech
  // during waiting/ready still drives nebula presence state (not only after Start).
  return (
    phase === 'waiting_for_examiner'
    || phase === 'ready_to_start'
    || phase === 'starting'
    || phase === 'between_questions'
    || phase === 'question_active'
  );
}

function ExamShellAbortConfirmButton({
  onAbort,
  isAborting = false,
  requiresEvaluation = true,
  committed = false,
}: {
  onAbort: () => void | Promise<void>;
  isAborting?: boolean;
  requiresEvaluation?: boolean;
  committed?: boolean;
}) {
  const { t } = useTranslation();
  const confirmAbort = useStudentExamAbortConfirm();

  const handleOpenDialog = useCallback(async () => {
    if (isAborting) return;
    const confirmed = await confirmAbort(t, requiresEvaluation, committed);
    if (confirmed) await onAbort();
  }, [committed, confirmAbort, isAborting, onAbort, requiresEvaluation, t]);

  return (
    <Button
      variant="outline"
      size="lg"
      action="negative"
      minHeight={48}
      onPress={handleOpenDialog}
      isDisabled={isAborting}
      testID="student-exam-abort"
    >
      {isAborting ? (
        <Spinner size="small" />
      ) : (
        <ButtonText>
          {t('oralExam.abortExam', { defaultValue: 'Abort exam' })}
        </ButtonText>
      )}
    </Button>
  );
}

function ExamShellAbortButton({
  onAbort,
  isAborting = false,
  requiresEvaluation = true,
  committed = false,
}: {
  onAbort?: () => void | Promise<void>;
  isAborting?: boolean;
  requiresEvaluation?: boolean;
  committed?: boolean;
}) {
  if (!onAbort) return null;
  return (
    <ExamShellAbortConfirmButton
      onAbort={onAbort}
      isAborting={isAborting}
      requiresEvaluation={requiresEvaluation}
      committed={committed}
    />
  );
}

const BETWEEN_QUESTIONS_PAUSED_HINT_MS = 2000;

function isOneToNExam(runState: OralExamRunStateResponse): boolean {
  return (runState.capacity ?? 1) > 1;
}

function isAgentReconnecting(
  runState: OralExamRunStateResponse,
  examinerUnavailable: boolean,
): boolean {
  if (!isOneToNExam(runState)) return false;
  // Cold-start still hard-stops; reconnect copy is only for a mid-exam drop.
  if (isExaminerColdStartConductPhase(runState.conductPhase)) return false;
  const fault = runState.operationalFault;
  if (
    fault === OralExamOperationalFault.failover_required
    || fault === OralExamOperationalFault.agent_failed
  ) {
    return true;
  }
  return examinerUnavailable && runState.state === 'in_progress';
}

function AgentReconnectingCopy({ testIdPrefix }: { testIdPrefix: string }) {
  const { t } = useTranslation();
  return (
    <>
      <Text
        size="xl"
        fontWeight="semibold"
        color="$textHeading"
        textAlign="center"
        testID={`${testIdPrefix}-title`}
      >
        {t('oralExam.student.examinerReconnecting.title', {
          defaultValue: 'Reconnecting',
        })}
      </Text>
      <Text
        size="md"
        color="$textBody"
        textAlign="center"
        testID={`${testIdPrefix}-body`}
      >
        {t('oralExam.student.examinerReconnecting.body', {
          defaultValue: 'Your answers are saved and your timer is paused.',
        })}
      </Text>
    </>
  );
}

function BetweenQuestionsHold({
  phase,
  presenceOverride,
  examinerUnavailable,
  agentReconnecting,
}: {
  phase: 'between_questions';
  presenceOverride?: AIPresenceState | null;
  examinerUnavailable?: boolean;
  agentReconnecting?: boolean;
}) {
  const { t } = useTranslation();
  const [showPausedHint, setShowPausedHint] = useState(false);

  useEffect(() => {
    if (phase !== 'between_questions') {
      setShowPausedHint(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowPausedHint(true), BETWEEN_QUESTIONS_PAUSED_HINT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  return (
    <VStack
      flex={1}
      alignItems="center"
      justifyContent="center"
      space="xl"
      width="100%"
      testID={studentExamShellPhaseTestId(phase)}
    >
      <AiExaminerPresence
        size={BETWEEN_QUESTIONS_ORB_SIZE}
        useLiveVoiceAssistant={orbActivatesVoiceAssistant(phase)}
        presenceOverride={presenceOverride}
      />
      <VStack space="sm" alignItems="center" maxWidth={560}>
        {agentReconnecting ? (
          <AgentReconnectingCopy testIdPrefix="oral-exam-agent-reconnecting" />
        ) : examinerUnavailable ? (
          <>
            <Text
              size="xl"
              fontWeight="semibold"
              color="$textHeading"
              textAlign="center"
              testID="oral-exam-examiner-unavailable"
            >
              {t('oralExam.student.examinerUnavailable.title', {
                defaultValue: 'Examiner unavailable',
              })}
            </Text>
            <Text
              size="md"
              color="$textBody"
              textAlign="center"
              testID="oral-exam-examiner-unavailable-body"
            >
              {t('oralExam.student.examinerUnavailable.body', {
                defaultValue:
                  'The AI examiner is not responding right now. Ask for help or abort the exam.',
              })}
            </Text>
          </>
        ) : (
          <>
            <Text
              size="xl"
              fontWeight="semibold"
              color="$textHeading"
              textAlign="center"
              testID="oral-exam-student-between-questions-title"
            >
              {t('oralExam.student.betweenQuestions.title', {
                defaultValue: 'Wrapping up',
              })}
            </Text>
            {showPausedHint ? (
              <Text
                size="md"
                color="$textBody"
                textAlign="center"
                testID="oral-exam-student-between-questions-paused-hint"
              >
                {t('oralExam.student.betweenQuestions.pausedHint', {
                  defaultValue: 'Your timer is paused.',
                })}
              </Text>
            ) : null}
          </>
        )}
      </VStack>
    </VStack>
  );
}

function PresenceRail({
  phase,
  runState,
  presenceOverride,
  examinerUnavailable,
  agentReconnecting,
  railWidth = PRESENCE_RAIL_WIDTH,
  orbSize = QUESTION_ACTIVE_ORB_SIZE,
  countdownRingSize = COMPACT_COUNTDOWN_RING_SIZE,
  topInset,
}: {
  phase: StudentExamShellPhase;
  runState: OralExamRunStateResponse;
  presenceOverride?: AIPresenceState | null;
  examinerUnavailable?: boolean;
  agentReconnecting?: boolean;
  railWidth?: number;
  orbSize?: number;
  countdownRingSize?: number;
  /** Aligns the orb with the top of the question article when it is placed. */
  topInset?: number;
}) {
  const { t } = useTranslation();
  const showCountdown = phase === 'question_active' && !examinerUnavailable && !agentReconnecting;

  return (
    <VStack
      testID="oral-exam-student-presence-rail"
      width={railWidth}
      flexShrink={0}
      alignItems="center"
      alignSelf="stretch"
      justifyContent="flex-start"
      bg="$bgSubtle"
      borderRightWidth={1}
      borderRightColor="$borderLight"
      pb="$6"
      px="$4"
    >
      {/* Shrinks before the orb and timer when the rail is short. */}
      <Box
        testID="oral-exam-student-rail-top-inset"
        height={topInset ?? RAIL_DEFAULT_TOP_INSET_PX}
        minHeight={16}
        flexShrink={1}
      />
      <VStack
        space={orbSize < QUESTION_ACTIVE_ORB_SIZE_NARROW ? 'md' : 'lg'}
        alignItems="center"
        alignSelf="stretch"
        flexShrink={0}
      >
        <AiExaminerPresence
          size={orbSize}
          useLiveVoiceAssistant={orbActivatesVoiceAssistant(phase)}
          presenceOverride={presenceOverride}
          preferListeningWhenSilent={phase === 'question_active'}
        />
        {agentReconnecting ? (
          <Text
            size="xs"
            color="$textSubtle"
            textAlign="center"
            testID="oral-exam-agent-reconnecting-rail"
          >
            {t('oralExam.student.examinerReconnecting.body', {
              defaultValue: 'Your answers are saved and your timer is paused.',
            })}
          </Text>
        ) : examinerUnavailable ? (
          <Text
            size="xs"
            color="$textSubtle"
            textAlign="center"
            testID="oral-exam-examiner-unavailable"
          >
            {t('oralExam.student.examinerUnavailable.railHint', {
              defaultValue: 'Ask for help or abort below.',
            })}
          </Text>
        ) : showCountdown ? (
          <Box
            testID="oral-exam-student-rail-countdown"
            mt={orbSize < QUESTION_ACTIVE_ORB_SIZE_NARROW ? '$5' : '$10'}
          >
            <AuthoritativeCountdown
              key={`${runState.currentQuestion?.questionId ?? 'none'}:${runState.questionDeadline ?? 'none'}`}
              ringSize={countdownRingSize}
              secondsPerQuestion={runState.secondsPerQuestion}
              questionMarkerStartedAt={runState.questionMarkerStartedAt}
              questionDeadline={runState.questionDeadline}
              isPaused={runState.isPaused ?? false}
              aiExaminer={
                runState.driver === ExamDriver.examiner_agent
              }
            />
          </Box>
        ) : null}
      </VStack>
    </VStack>
  );
}

function StartExamCta({
  onStartExam,
  startDisabled,
  showStartingFooter,
  startExamError,
  waitingForExaminer,
}: {
  onStartExam?: () => void;
  startDisabled: boolean;
  showStartingFooter: boolean;
  startExamError: string | null;
  waitingForExaminer: boolean;
}) {
  const { t } = useTranslation();

  return (
    <VStack space="md" alignItems="center" testID="oral-exam-student-start-cta">
      <Button
        size="lg"
        minHeight={48}
        minWidth={220}
        action="primary"
        onPress={onStartExam}
        isDisabled={startDisabled}
        testID="oral-exam-student-start-exam"
        accessibilityState={{ disabled: startDisabled }}
      >
        {showStartingFooter ? (
          <>
            <ButtonSpinner mr="$2" />
            <ButtonText>
              {t('oralExam.student.examShell.startingFooter', {
                defaultValue: 'Starting…',
              })}
            </ButtonText>
          </>
        ) : (
          <ButtonText>
            {t('oralExam.student.examShell.startExam', {
              defaultValue: 'Start exam',
            })}
          </ButtonText>
        )}
      </Button>
      {waitingForExaminer ? (
        <Text size="sm" color="$textSubtle" testID="oral-exam-student-start-waiting-hint">
          {t('oralExam.student.examShell.waitingForExaminerHint', {
            defaultValue: 'Waiting for the AI examiner…',
          })}
        </Text>
      ) : null}
      {startExamError ? (
        <Text size="sm" color="$statusError" testID="oral-exam-student-start-error">
          {startExamError}
        </Text>
      ) : null}
    </VStack>
  );
}

function PreQuestionHold({
  phase,
  studentFirstName,
  presenceOverride,
  examinerUnavailable,
  onStartExam,
  startDisabled,
  showStartingFooter,
  startExamError,
  waitingForExaminer,
}: {
  phase: Exclude<StudentExamShellPhase, 'question_active' | 'between_questions'>;
  studentFirstName: string;
  presenceOverride?: AIPresenceState | null;
  examinerUnavailable?: boolean;
  onStartExam?: () => void;
  startDisabled: boolean;
  showStartingFooter: boolean;
  startExamError: string | null;
  waitingForExaminer: boolean;
}) {
  const { t } = useTranslation();
  const subcopyKeyByPhase: Record<typeof phase, string> = {
    waiting_for_examiner: 'oralExam.student.examShell.waitingSubcopy',
    ready_to_start: 'oralExam.student.examShell.readySubcopy',
    starting: 'oralExam.student.examShell.startingSubcopy',
  };
  const subcopyDefaultByPhase: Record<typeof phase, string> = {
    waiting_for_examiner: 'The AI examiner is joining your room — Start unlocks when they are ready.',
    ready_to_start: 'The AI examiner is ready. Start when you are.',
    starting: 'Starting…',
  };

  return (
    <Box
      flex={1}
      minHeight={0}
      width="100%"
      justifyContent="center"
      alignItems="center"
      px="$6"
      testID={studentExamShellPhaseTestId(phase)}
    >
      <VStack alignItems="center" space="2xl" maxWidth={560} width="100%">
        <VStack
          alignItems="center"
          space="2xl"
          width="100%"
          testID="oral-exam-student-handshake-presence"
        >
          <Box
            testID="oral-exam-student-ai-examiner-orb-footprint"
            width={BETWEEN_QUESTIONS_ORB_SIZE}
            height={BETWEEN_QUESTIONS_ORB_SIZE}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <AiExaminerPresence
              size={BETWEEN_QUESTIONS_ORB_SIZE}
              useLiveVoiceAssistant={orbActivatesVoiceAssistant(phase)}
              presenceOverride={presenceOverride}
            />
          </Box>
          {examinerUnavailable ? (
            <VStack space="sm" alignItems="center">
              <Text
                size="2xl"
                fontWeight="semibold"
                color="$textHeading"
                textAlign="center"
                testID="oral-exam-examiner-unavailable"
              >
                {t('oralExam.student.examinerUnavailable.title', {
                  defaultValue: 'Examiner unavailable',
                })}
              </Text>
              <Text
                size="md"
                color="$textBody"
                textAlign="center"
                testID="oral-exam-examiner-unavailable-body"
              >
                {t('oralExam.student.examinerUnavailable.body', {
                  defaultValue:
                    'The AI examiner is not responding right now. Ask for help or abort the exam.',
                })}
              </Text>
            </VStack>
          ) : (
            <VStack space="sm" alignItems="center">
              <Text
                size="2xl"
                fontWeight="semibold"
                color="$textHeading"
                textAlign="center"
                testID="oral-exam-student-welcome-heading"
              >
                {t('oralExam.student.examShell.welcome', {
                  firstName: studentFirstName,
                  defaultValue: 'Welcome, {{firstName}}',
                })}
              </Text>
              <Text
                size="md"
                color="$textBody"
                textAlign="center"
                testID="oral-exam-student-status-subcopy"
              >
                {t(subcopyKeyByPhase[phase], {
                  defaultValue: subcopyDefaultByPhase[phase],
                })}
              </Text>
            </VStack>
          )}
        </VStack>
        {examinerUnavailable ? null : (
          <Box pt="$6">
            <StartExamCta
              onStartExam={onStartExam}
              startDisabled={startDisabled}
              showStartingFooter={showStartingFooter}
              startExamError={startExamError}
              waitingForExaminer={waitingForExaminer}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
}

function ExamShellActionBar({
  phase,
  runState,
  driver,
  onAbort,
  isAborting,
  onStudentDone,
  onRequestHelp,
  isSignalingDone,
  isRequestingHelp,
  studentDoneError = null,
  examinerUnavailable,
  agentReconnecting = false,
  connectionPause = false,
  mediaPauseBlocking = false,
}: {
  phase: StudentExamShellPhase;
  runState: OralExamRunStateResponse;
  driver: OralExamDriver;
  onAbort?: () => void | Promise<void>;
  isAborting?: boolean;
  onStudentDone?: () => void;
  onRequestHelp?: () => void;
  isSignalingDone?: boolean;
  isRequestingHelp?: boolean;
  studentDoneError: string | null;
  examinerUnavailable?: boolean;
  agentReconnecting?: boolean;
  connectionPause?: boolean;
  mediaPauseBlocking?: boolean;
}) {
  if (agentReconnecting || connectionPause) {
    return null;
  }
  if (phase === 'question_active') {
    return (
      <HStack
        testID="oral-exam-student-action-bar"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        py="$4"
        px="$4"
        minHeight={88}
      >
        <HStack space="md" alignItems="center" flex={1}>
          <ExamShellAbortButton
            onAbort={onAbort}
            isAborting={isAborting}
            requiresEvaluation={runRequiresEvaluation(runState)}
            committed={runState.committed ?? false}
          />
          {onRequestHelp ? (
            <NeedHelpButton
              onPress={onRequestHelp}
              isPaused={runState.isPaused ?? false}
              isRequestingHelp={isRequestingHelp}
            />
          ) : null}
        </HStack>
        {examinerUnavailable || mediaPauseBlocking ? null : (
          <VStack space="xs" alignItems="flex-end">
            <DoneAffordance
              driver={driver}
              secondsPerQuestion={runState.secondsPerQuestion}
              questionMarkerStartedAt={runState.questionMarkerStartedAt}
              questionDeadline={runState.questionDeadline}
              studentDoneAt={runState.studentDoneAt}
              isPaused={runState.isPaused ?? false}
              isSignalingDone={isSignalingDone}
              onPress={onStudentDone}
            />
            {studentDoneError ? (
              <Text size="sm" color="$statusError" testID="oral-exam-student-done-error">
                {studentDoneError}
              </Text>
            ) : null}
          </VStack>
        )}
      </HStack>
    );
  }

  if (phase === 'between_questions' || examinerUnavailable) {
    return (
      <HStack
        testID="oral-exam-student-action-bar"
        width="100%"
        alignItems="center"
        py="$4"
        px="$4"
        minHeight={88}
        space="md"
      >
        <ExamShellAbortButton
          onAbort={onAbort}
          isAborting={isAborting}
          requiresEvaluation={runRequiresEvaluation(runState)}
          committed={runState.committed ?? false}
        />
        <NeedHelpButton
          onPress={onRequestHelp}
          isPaused={runState.isPaused ?? false}
          isRequestingHelp={isRequestingHelp}
        />
      </HStack>
    );
  }

  return null;
}

/** Persistent pre-Q1 / in-question shell with five fixed landmark regions (R3). */
export function StudentExamShell({
  runState,
  studentFirstName,
  driver = 'examiner_agent',
  onStartExam,
  isMarkingStudentReady = false,
  startExamError = null,
  studentDoneError = null,
  onStudentDone,
  onOpenAnswerWindow,
  onRequestHelp,
  isSignalingDone = false,
  isRequestingHelp = false,
  isReconnecting = false,
  liveKitConnectionState,
  isScreenSharing = false,
  bookingId,
  supervisorName,
  supervisorPhotoUrl,
  supervisorFaces = [],
  visitingLecturerId,
  openMediaSources = [],
  permissionDeniedSource = null,
  onReshareScreen,
  onEnableCamera,
  onUnmuteMic,
  onAbort,
  isAborting = false,
  onLeave,
  onStartPractice,
  isStartingPractice = false,
  startPracticeError = null,
}: StudentExamShellProps) {
  const { t } = useTranslation();
  const rawPhase = deriveStudentExamShellPhase(
    runState.conductPhase,
    runState.currentQuestion,
    runState.currentQuestionIndex,
  );
  const heldQuestionRef = useRef(runState.currentQuestion);
  if (runState.currentQuestion) {
    heldQuestionRef.current = runState.currentQuestion;
  }
  const betweenQuestionsRevealMs = runState.studentDoneAt ? 0 : BETWEEN_QUESTIONS_REVEAL_MS;
  const phase = useDisplayedExamShellPhase(
    rawPhase,
    Boolean(heldQuestionRef.current),
    betweenQuestionsRevealMs,
  );
  const displayQuestion = runState.currentQuestion ?? (
    phase === 'question_active' ? heldQuestionRef.current : null
  );
  const prevQuestionClockRef = useRef<{
    questionId: string | null;
    index: number;
    deadline: string | null;
  } | null>(null);
  const staleDeadlineWarnedRef = useRef<string | null>(null);
  const [shellLayout, setShellLayout] = useState<{ width: number; height: number } | null>(null);
  const [questionViewportHeight, setQuestionViewportHeight] = useState<number | null>(null);
  const [questionTopGap, setQuestionTopGap] = useState<number | null>(null);

  useEffect(() => {
    const questionId = runState.currentQuestion?.questionId ?? null;
    const index = runState.currentQuestionIndex ?? 0;
    const deadline = runState.questionDeadline ?? null;
    const prev = prevQuestionClockRef.current;

    if (prev && prev.index !== index) {
      staleDeadlineWarnedRef.current = null;
    }

    prevQuestionClockRef.current = { questionId, index, deadline };

    if (phase !== 'question_active' || !deadline) return;

    const staleByMs = Date.now() - new Date(deadline).getTime();
    if (staleByMs <= 5000) return;

    const warnKey = `${index}:${deadline}`;
    if (staleDeadlineWarnedRef.current === warnKey) return;

    staleDeadlineWarnedRef.current = warnKey;
    log.warn('[StudentExamShell] question deadline elapsed without index advance', {
      bookingId,
      questionId,
      currentQuestionIndex: index,
      questionDeadline: deadline,
      staleBySeconds: Math.floor(staleByMs / 1000),
    });
  }, [
    bookingId,
    phase,
    runState.currentQuestion?.questionId,
    runState.currentQuestionIndex,
    runState.questionDeadline,
  ]);
  const isCohort = runState.mode === OralExamMode.one_to_n;
  const isNarrowShell = shellLayout != null && (
    shellLayout.width < SHELL_NARROW_MAX_WIDTH
    || shellLayout.height < SHELL_NARROW_MAX_HEIGHT
  );
  const isCompactShell = shellLayout != null && (
    shellLayout.width < SHELL_COMPACT_MAX_WIDTH
    || shellLayout.height < SHELL_COMPACT_MAX_HEIGHT
  );
  const presenceRailWidth = isNarrowShell ? PRESENCE_RAIL_WIDTH_NARROW : PRESENCE_RAIL_WIDTH;
  const questionOrbSize = isCompactShell
    ? QUESTION_ACTIVE_ORB_SIZE_COMPACT
    : (isNarrowShell ? QUESTION_ACTIVE_ORB_SIZE_NARROW : QUESTION_ACTIVE_ORB_SIZE);
  const countdownRingSize = isNarrowShell
    ? COMPACT_COUNTDOWN_RING_SIZE_NARROW
    : COMPACT_COUNTDOWN_RING_SIZE;
  const showQuestionProgress = runState.conductPhase === OralExamConductPhase.in_exam
    && (Boolean(displayQuestion) || phase === 'between_questions');
  const connectionDown = openMediaSources.includes(OralExamMediaSource.connection);
  const recordingDown = isRecordingIntegrityOpen(openMediaSources)
    || isRecordingIntegrityOpenInRunState(runState);
  const mediaDeviceDown = clientRestorableOpenSources(openMediaSources);
  const inOfficialIntegrityExam = phase === 'question_active' || phase === 'between_questions';
  const fullLiveKitReconnect = inOfficialIntegrityExam && (
    liveKitConnectionState === ConnectionState.Reconnecting
    || (
      liveKitConnectionState === ConnectionState.Disconnected
      && isReconnecting
    )
  );
  const examinerFailoverPause = isOneToNExam(runState)
    && runState.operationalFault === OralExamOperationalFault.failover_required;
  const technicalConnectionPause = Boolean(
    runState.isPaused
    && runState.pauseReason === OralExamPauseReason.technical
    && !examinerFailoverPause
    && mediaDeviceDown.length === 0
    && !recordingDown,
  );
  const connectionPauseActive = connectionDown || technicalConnectionPause || fullLiveKitReconnect;
  const connectionRestoredLocally = liveKitConnectionState === ConnectionState.Connected
    && connectionPauseActive
    && !fullLiveKitReconnect;
  const mediaDown = connectionPauseActive
    || recordingDown
    || (!isReconnecting && mediaDeviceDown.length > 0);
  const supervisorStatus = runState.supervisorStatus ?? OralExamSupervisorStatus.watching;
  const cohortQuestionColumnPr = resolveCohortQuestionColumnPrPx(supervisorStatus, isCompactShell);
  const engagedLecturerPause = isSupervisorEngagedLecturerPause({
    isPaused: runState.isPaused,
    pauseReason: runState.pauseReason,
    supervisorStatus,
  });
  const showPauseOverlay = Boolean(
    connectionPauseActive
    || recordingDown
    || (!isReconnecting && (
      (runState.isPaused && runState.pauseReason && !examinerFailoverPause)
      || mediaDeviceDown.length > 0
    )),
  ) && !(engagedLecturerPause && !mediaDown);
  const overlayPauseReason = connectionPauseActive
    ? OralExamPauseReason.technical
    : (runState.pauseReason ?? OralExamPauseReason.media_disabled);
  const mediaPauseBlocking = showPauseOverlay
    && !connectionPauseActive
    && overlayPauseReason === OralExamPauseReason.media_disabled;
  const showTopBannerPauseOverlay = showPauseOverlay && !mediaPauseBlocking;
  const trimmedFirstName = studentFirstName.trim() || t('oralExam.student.examShell.studentFallback', {
    defaultValue: 'student',
  });

  const ctaGate = resolveStudentCtaGate({
    role: 'student',
    sessionPhase: runState.sessionPhase,
    runState: runState.state,
    conductPhase: runState.conductPhase,
    isPaused: runState.isPaused,
    pauseReason: runState.pauseReason,
    studentDoneAt: runState.studentDoneAt,
    currentQuestion: runState.currentQuestion,
    currentQuestionIndex: runState.currentQuestionIndex,
    isMarkingStudentReady,
  });
  const startDisabled = !ctaGate.startExam;
  const showStartingFooter = phase === 'starting' || isMarkingStudentReady;
  const showSupervisorFrame = isCohort
    && !isSelfServiceRun(runState)
    && Boolean(bookingId && (supervisorName || supervisorFaces.length > 0));
  const supervisorFrameLayoutPhase = phase === 'question_active' ? 'question_active' : 'hold';

  const { state: voiceAssistantState, audioTrack } = useVoiceAssistant();
  const orbState = resolveExaminerOrbState(
    voiceAssistantState,
    audioTrack ?? null,
    { preferListeningWhenSilent: phase === 'question_active' },
  );
  const voiceSpeaking = orbState === 'speaking'
    || hasLiveAgentAudioTrack(audioTrack ?? null);
  // Clock/Done are server-owned. Open as soon as LiveKit leaves `speaking` —
  // do not wait for a lingering audio publication (that delay is what the
  // student hears as "TTS ended but the timer hasn't started").
  const examinerReadingAloud = voiceAssistantState === 'speaking';
  const spokenQuestionIdsRef = useRef<Set<string>>(new Set());
  const answerWindowInFlightRef = useRef<string | null>(null);

  useEffect(() => {
    const questionId = runState.currentQuestion?.questionId;
    if (phase === 'question_active' && questionId && examinerReadingAloud) {
      spokenQuestionIdsRef.current.add(questionId);
    }
  }, [phase, runState.currentQuestion?.questionId, examinerReadingAloud]);

  useEffect(() => {
    answerWindowInFlightRef.current = null;
  }, [runState.currentQuestion?.questionId]);

  useEffect(() => {
    const questionId = runState.currentQuestion?.questionId ?? null;
    if (
      !onOpenAnswerWindow
      || phase !== 'question_active'
      || !questionId
      || runState.questionDeadline
      || examinerReadingAloud
      || !spokenQuestionIdsRef.current.has(questionId)
    ) {
      return;
    }
    if (answerWindowInFlightRef.current === questionId) {
      return;
    }
    answerWindowInFlightRef.current = questionId;
    Promise.resolve(onOpenAnswerWindow()).catch(() => {
      if (answerWindowInFlightRef.current === questionId) {
        answerWindowInFlightRef.current = null;
      }
    });
  }, [
    onOpenAnswerWindow,
    phase,
    runState.currentQuestion?.questionId,
    runState.questionDeadline,
    examinerReadingAloud,
  ]);
  const examinerPresent = useExaminerAgentPresent();
  const { unavailable: examinerUnavailable } = useExaminerAgentWatchdog({
    phase,
    studentDoneAt: runState.studentDoneAt,
    currentQuestionIndex: runState.currentQuestionIndex ?? 0,
    currentQuestionId: runState.currentQuestion?.questionId ?? null,
    voiceSpeaking,
    agentPresent: examinerPresent,
    examinerWorking: orbState === 'thinking' || orbState === 'listening',
    isPaused: runState.isPaused ?? false,
    supervisorVisible: supervisorStatus === OralExamSupervisorStatus.here_with_you
      || supervisorStatus === OralExamSupervisorStatus.engaged,
  });
  const showAgentReconnecting = isAgentReconnecting(runState, examinerUnavailable);
  const showLegacyUnavailable = examinerUnavailable && !showAgentReconnecting;
  const showTransportReconnectOverlay = useTransportReconnectOverlayVisible(isReconnecting)
    && !connectionPauseActive;
  if (runState.state === 'aborted' && runState.endReason === 'session_void') {
    return (
      <SessionVoidTechnicalScreen
        onLeave={() => {
          if (onLeave) {
            onLeave();
            return;
          }
          onAbort?.();
        }}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }
  if (shouldShowAgentUnavailableTerminal(runState)) {
    return (
      <AgentUnavailableTerminalScreen
        runState={runState}
        onStartDryRun={onStartPractice}
        isStartingDryRun={isStartingPractice}
        startDryRunError={startPracticeError}
        leave={() => {
          if (onLeave) {
            onLeave();
            return;
          }
          onAbort?.();
        }}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }
  const examinerColdStartHardStop = shouldExaminerUnavailableHardStop(phase, showLegacyUnavailable);
  if (examinerColdStartHardStop) {
    return (
      <OralExamErrorState
        testID="oral-exam-examiner-cold-start-error"
        title={t('oralExam.student.examinerUnavailable.title', {
          defaultValue: 'Examiner unavailable',
        })}
        message={t('oralExam.student.examinerUnavailable.hardStopBody', {
          defaultValue:
            'The AI examiner did not join in time. Leave and rejoin from your bookings, or ask your supervisor for help.',
        })}
        tone="warning"
        leave={() => {
          if (onLeave) {
            onLeave();
            return;
          }
          onAbort?.();
        }}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }
  // Reconnecting caption is LiveKit transport only — never map examiner silence to error.
  const presenceOverride: AIPresenceState | null = null;
  const isTransportConnected = !isReconnecting;
  const questionColumnPt = isCohort ? COHORT_QUESTION_COLUMN_PT : QUESTION_COLUMN_PT;
  const questionPlaced = !showAgentReconnecting && !showLegacyUnavailable && !mediaPauseBlocking;
  const railTopInset = questionPlaced && questionTopGap != null
    ? questionColumnPt + questionTopGap
    : undefined;

  return (
    <Box
      position="relative"
      flex={1}
      minHeight={0}
      testID="oral-exam-student-exam-shell"
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        setShellLayout((prev) => (
          prev?.width === width && prev?.height === height
            ? prev
            : { width, height }
        ));
      }}
    >
      {liveKitConnectionState ? (
        <Box
          testID={`oral-exam-livekit-connection-${liveKitConnectionState}`}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          width={0}
          height={0}
          overflow="hidden"
        />
      ) : null}
      <VStack
        px="$5"
        pt="$4"
        space="lg"
        flex={1}
        minHeight={0}
        testID="oral-exam-student-in-progress"
      >
        <Box
          pb="$3"
          borderBottomWidth={1}
          borderBottomColor="$borderLight"
        >
          <QuestionProgressStrip
            isRecording={
              runState.recordingRequired === true
              && runState.recordingStatus === 'pending'
              && !recordingDown
            }
            isRecordingRestarting={recordingDown}
            isConnected={isTransportConnected}
            currentQuestionIndex={runState.currentQuestionIndex}
            totalQuestions={oralExamAskedQuestionCount(runState)}
            showQuestionProgress={showQuestionProgress}
          />
        </Box>

        {showTopBannerPauseOverlay ? (
          <PauseOverlay
            pauseReason={overlayPauseReason}
            openMediaSources={openMediaSources}
            remainingConsecutiveSeconds={integrityBudgetPauseRemainingSeconds(runState.integrityBudget)}
            connectionRestoredLocally={connectionRestoredLocally}
            permissionDeniedSource={permissionDeniedSource}
            onReshareScreen={onReshareScreen}
            onEnableCamera={onEnableCamera}
            onUnmuteMic={onUnmuteMic}
          />
        ) : null}

        <Box
          position="relative"
          flex={1}
          minHeight={0}
          width="100%"
          testID="oral-exam-student-phase-body"
        >
          {mediaPauseBlocking && phase !== 'question_active' ? (
            <PauseOverlay
              variant="blocking"
              pauseReason={overlayPauseReason}
              openMediaSources={openMediaSources}
              remainingConsecutiveSeconds={integrityBudgetPauseRemainingSeconds(runState.integrityBudget)}
              connectionRestoredLocally={connectionRestoredLocally}
              permissionDeniedSource={permissionDeniedSource}
              onReshareScreen={onReshareScreen}
              onEnableCamera={onEnableCamera}
              onUnmuteMic={onUnmuteMic}
            />
          ) : phase === 'question_active' ? (
            <HStack
              flex={1}
              width="100%"
              alignItems="stretch"
              minHeight={0}
              overflow="hidden"
              borderWidth={1}
              borderColor="$borderLight"
              borderRadius="$md"
              testID={studentExamShellPhaseTestId(phase)}
            >
              <PresenceRail
                phase={phase}
                runState={runState}
                presenceOverride={presenceOverride}
                examinerUnavailable={showLegacyUnavailable}
                agentReconnecting={showAgentReconnecting}
                railWidth={presenceRailWidth}
                orbSize={questionOrbSize}
                countdownRingSize={countdownRingSize}
                topInset={railTopInset}
              />
              <Box
                testID="oral-exam-student-question-column"
                flex={1}
                minWidth={320}
                minHeight={0}
                pt={questionColumnPt}
                pb={isCohort ? COHORT_QUESTION_COLUMN_PT : '$8'}
                pl={isCohort ? 32 : 100}
                pr={isCohort ? cohortQuestionColumnPr : QUESTION_COLUMN_PR_PX}
                justifyContent="flex-start"
                position="relative"
                bg="$bgApp"
              >
                <Box
                  flex={1}
                  minHeight={0}
                  onLayout={(event) => {
                    const { height } = event.nativeEvent.layout;
                    setQuestionViewportHeight((prev) => (prev === height ? prev : height));
                  }}
                  sx={{
                    _web: {
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      scrollbarGutter: 'auto',
                    },
                  }}
                  testID="oral-exam-student-question-scroll"
                >
                  {showAgentReconnecting ? (
                    <VStack space="md" maxWidth={640} testID="oral-exam-agent-reconnecting-panel">
                      <AgentReconnectingCopy testIdPrefix="oral-exam-agent-reconnecting" />
                    </VStack>
                  ) : showLegacyUnavailable ? (
                    <VStack space="md" maxWidth={640} testID="oral-exam-examiner-unavailable-panel">
                      <Text size="xl" fontWeight="semibold" color="$textHeading">
                        {t('oralExam.student.examinerUnavailable.title', {
                          defaultValue: 'Examiner unavailable',
                        })}
                      </Text>
                      <Text size="md" color="$textBody" testID="oral-exam-examiner-unavailable-body">
                        {t('oralExam.student.examinerUnavailable.body', {
                          defaultValue:
                            'The AI examiner is not responding right now. Ask for help or abort the exam.',
                        })}
                      </Text>
                    </VStack>
                  ) : mediaPauseBlocking ? (
                    <PauseOverlay
                      variant="blocking"
                      pauseReason={overlayPauseReason}
                      openMediaSources={openMediaSources}
                      remainingConsecutiveSeconds={integrityBudgetPauseRemainingSeconds(runState.integrityBudget)}
                      connectionRestoredLocally={connectionRestoredLocally}
                      permissionDeniedSource={permissionDeniedSource}
                      onReshareScreen={onReshareScreen}
                      onEnableCamera={onEnableCamera}
                      onUnmuteMic={onUnmuteMic}
                    />
                  ) : (
                    <VStack
                      minHeight="100%"
                      width="100%"
                      testID="oral-exam-student-question-positioner"
                    >
                      {/* Flexible gutters place the story slightly above centre and
                          collapse before content on low-height screens, preserving scroll. */}
                      <Box
                        flex={2}
                        minHeight={0}
                        testID="oral-exam-student-question-top-gap"
                        onLayout={(event) => {
                          const { height } = event.nativeEvent.layout;
                          setQuestionTopGap((prev) => (prev === height ? prev : height));
                        }}
                      />
                      <QuestionHero
                        question={displayQuestion?.question}
                        showOrb={false}
                        activateVoiceAssistant
                        adaptiveTextSize={isCohort}
                        viewportHeight={questionViewportHeight}
                      />
                      <Box flex={3} minHeight={0} />
                    </VStack>
                  )}
                </Box>
                {/* Self-view sits in the question column above the footer Done (mockup). */}
                {isCohort ? (
                  <SelfViewChip
                    testID="oral-exam-self-view-chip"
                    sharing={isScreenSharing}
                  />
                ) : null}
              </Box>
            </HStack>
          ) : phase === 'between_questions' ? (
            <BetweenQuestionsHold
              phase={phase}
              presenceOverride={presenceOverride}
              examinerUnavailable={showLegacyUnavailable}
              agentReconnecting={showAgentReconnecting}
            />
          ) : (
            <PreQuestionHold
              phase={phase}
              studentFirstName={trimmedFirstName}
              presenceOverride={presenceOverride}
              examinerUnavailable={showLegacyUnavailable}
              onStartExam={onStartExam}
              startDisabled={startDisabled}
              showStartingFooter={showStartingFooter}
              startExamError={startExamError}
              waitingForExaminer={phase === 'waiting_for_examiner'}
            />
          )}

          {showSupervisorFrame ? (
            <SupervisorFrame
              supervisorName={supervisorName ?? supervisorFaces[0]?.name ?? 'Supervisor'}
              supervisorPhotoUrl={supervisorPhotoUrl}
              supervisorFaces={supervisorFaces}
              visitingLecturerId={visitingLecturerId}
              bookingId={bookingId}
              status={supervisorStatus}
              layoutPhase={supervisorFrameLayoutPhase}
              questionColumnTopInset={
                supervisorFrameLayoutPhase === 'question_active'
                  ? COHORT_QUESTION_COLUMN_PT
                  : undefined
              }
              showEngagedPauseCue={engagedLecturerPause}
              compact={isCompactShell}
            />
          ) : null}
        </Box>

        {phase === 'question_active' || phase === 'between_questions' || showLegacyUnavailable ? (
          <ExamShellActionBar
            phase={phase}
            runState={runState}
            driver={driver}
            onAbort={onAbort}
            isAborting={isAborting}
            onStudentDone={onStudentDone}
            onRequestHelp={onRequestHelp}
            isSignalingDone={isSignalingDone}
            isRequestingHelp={isRequestingHelp}
            studentDoneError={studentDoneError}
            examinerUnavailable={showLegacyUnavailable || showAgentReconnecting}
            agentReconnecting={showAgentReconnecting}
            connectionPause={connectionPauseActive}
            mediaPauseBlocking={mediaPauseBlocking}
          />
        ) : null}
      </VStack>

      <ReconnectOverlay visible={showTransportReconnectOverlay} />
    </Box>
  );
}
